import type {
  Route,
  RouteDestination,
  RouteEmergency,
  RouteEvent,
  RouteTransportation,
} from './route';
import { isKnownTransport } from '../lib/transportation';

/**
 * Pure merge helpers shared by the socket listeners (socket.ts) and the
 * reconciliation poll (routePoll.ts). Everything here takes the current
 * route and an incoming partial and returns a new Route — no signals, no
 * side effects — so the merge rules are unit-testable without a socket.
 *
 * The golden rule (confirmed by arrivesafe-react-native #99): server
 * payloads are PARTIAL. The `route` socket event is the API's
 * FormattedRoute with only its defined fields — it never carries
 * `waypoints` or `events`, so replacing the store with it would wipe the
 * trail and the journey log. Merge, never replace.
 */

/**
 * Shape of the `route` socket event (api #156/#159) and any other partial
 * route document. `id` is what FormattedRoute actually sends; `route_id`
 * is tolerated for older emitters.
 */
export type RoutePartial = {
  id?: string;
  route_id?: string;
  title?: string | null;
  finished?: boolean;
  finished_at?: string | null;
  address_start?: string | null;
  address_arrival?: string | null;
  distance?: number;
  duration?: number;
  average_speed?: number;
  destination?: RouteDestination | null;
  /**
   * The `route` event projects transportation down to `{ currentType }` —
   * merging must not clobber `updatedAt`/`currentLat` learned from a prior
   * full `transportation` event.
   */
  transportation?: Partial<RouteTransportation> | null;
  emergency?: RouteEmergency | null;
};

/** `emergency` socket event per api #143 — flat, not nested under a key. */
export type EmergencyEventPayload = {
  routeID?: string;
  state?: string;
  triggeredAt?: string | Date;
  clearedAt?: string | Date;
  location?: { lat?: number; lng?: number; address?: string };
  triggeredBy?: { userID: string; name?: string };
};

/** `transportation` socket event per api #149 (+ lat/lng from #151). */
export type TransportationEventPayload = {
  routeID?: string;
  currentType?: string;
  currentSource?: string;
  currentConfidence?: number;
  currentLat?: number;
  currentLng?: number;
  updatedAt?: string | Date;
};

function toIsoString(value: string | Date): string {
  return value instanceof Date ? value.toISOString() : value;
}

export function mergeRoutePartial(current: Route, partial: RoutePartial): Route {
  const distance = partial.distance ?? current.distance;
  const duration = partial.duration ?? current.duration;
  const averageSpeed = partial.average_speed ?? current.average_speed;

  // Field semantics throughout: `undefined` means "not in this payload,
  // keep what we know" (FormattedRoute omits undefined fields); an explicit
  // `null` means "the server cleared this" and must win — coalescing with
  // `??` would leave stale data on screen after a clear edit.
  let transportation = current.transportation ?? null;
  const incomingTransport = partial.transportation;
  if (incomingTransport === null) {
    transportation = null;
  } else if (incomingTransport && typeof incomingTransport.currentType === 'string') {
    // Unknown future modes clear the subdoc: hiding the chip is honest,
    // keeping the previous mode on screen is not.
    transportation = isKnownTransport(incomingTransport.currentType)
      ? {
          ...current.transportation,
          ...incomingTransport,
          currentType: incomingTransport.currentType,
        }
      : null;
  }

  // Emergency is the one subdoc where an explicit null is deliberately
  // ignored: the API ends an emergency by state transition ('active' →
  // 'cleared'), never by nulling the subdoc, and honoring a stray null
  // would hide an active banner or cut the cleared banner's sticky window
  // short. Safety-critical state only moves forward through known shapes.
  let emergency = current.emergency ?? null;
  if (partial.emergency) {
    // The socket never sends alertedCount (#180: REST reads only) — keep
    // the value a poll already learned rather than dropping it.
    const alertedCount = partial.emergency.alertedCount ?? current.emergency?.alertedCount;
    emergency = {
      ...partial.emergency,
      ...(alertedCount !== undefined && { alertedCount }),
    };
  }

  return {
    ...current,
    finished: partial.finished ?? current.finished,
    finished_at: partial.finished_at !== undefined ? partial.finished_at : current.finished_at ?? null,
    title: partial.title !== undefined ? partial.title : current.title ?? null,
    address_start:
      partial.address_start !== undefined ? partial.address_start : current.address_start ?? null,
    address_arrival:
      partial.address_arrival !== undefined
        ? partial.address_arrival
        : current.address_arrival ?? null,
    destination: partial.destination !== undefined ? partial.destination : current.destination ?? null,
    transportation,
    emergency,
    ...(distance !== undefined && { distance }),
    ...(duration !== undefined && { duration }),
    ...(averageSpeed !== undefined && { average_speed: averageSpeed }),
  };
}

/**
 * Applies an `emergency` socket event. Returns the route unchanged when the
 * payload isn't renderable: EmergencyBanner reads `emergency.location`
 * unconditionally, so without a location (from the payload or a previous
 * state) we skip and let the reconciliation poll backfill.
 */
export function applyEmergencyEvent(current: Route, payload: EmergencyEventPayload): Route {
  if (payload.state !== 'active' && payload.state !== 'cleared') return current;
  const base = current.emergency ?? null;
  const lat = payload.location?.lat;
  const lng = payload.location?.lng;
  const location =
    typeof lat === 'number' && typeof lng === 'number'
      ? {
          lat,
          lng,
          ...(payload.location?.address !== undefined && { address: payload.location.address }),
        }
      : base?.location;
  if (!location) return current;
  const triggeredAt = payload.triggeredAt
    ? toIsoString(payload.triggeredAt)
    : base?.triggeredAt ?? new Date().toISOString();
  // Cleared banners auto-hide 5 min after `clearedAt`; without one they
  // would stick forever, so fall back to receipt time.
  const clearedAt = payload.clearedAt
    ? toIsoString(payload.clearedAt)
    : payload.state === 'cleared'
      ? base?.clearedAt ?? new Date().toISOString()
      : base?.clearedAt;
  const triggeredBy = payload.triggeredBy ?? base?.triggeredBy ?? { userID: 'unknown' };
  const alertedCount = base?.alertedCount;
  const emergency: RouteEmergency = {
    state: payload.state,
    triggeredAt,
    location,
    triggeredBy,
    ...(clearedAt !== undefined && { clearedAt }),
    ...(alertedCount !== undefined && { alertedCount }),
  };
  return { ...current, emergency };
}

/**
 * Applies a `transportation` socket event. A mode outside the known union
 * clears the subdoc (the chip hides) instead of keeping the previous mode
 * on screen; payloads without a usable type are ignored.
 */
export function applyTransportationEvent(
  current: Route,
  payload: TransportationEventPayload,
): Route {
  if (typeof payload.currentType !== 'string' || payload.currentType === '') return current;
  if (!isKnownTransport(payload.currentType)) return { ...current, transportation: null };
  const currentSource =
    payload.currentSource === 'auto' || payload.currentSource === 'manual'
      ? payload.currentSource
      : undefined;
  const transportation: RouteTransportation = {
    ...current.transportation,
    currentType: payload.currentType,
    ...(currentSource !== undefined && { currentSource }),
    ...(payload.currentConfidence !== undefined && {
      currentConfidence: payload.currentConfidence,
    }),
    ...(payload.currentLat !== undefined && { currentLat: payload.currentLat }),
    ...(payload.currentLng !== undefined && { currentLng: payload.currentLng }),
    ...(payload.updatedAt !== undefined && { updatedAt: toIsoString(payload.updatedAt) }),
  };
  return { ...current, transportation };
}

/**
 * Dedupe key for journey events. `route-event` broadcasts the pre-save doc
 * (no `_id`, api #180), and the reconciliation poll re-delivers the same
 * events from the route read — type+time identifies an event across both.
 */
export function routeEventKey(event: RouteEvent): string {
  return `${event.type}|${event.time ? toIsoString(event.time) : ''}`;
}

export function appendRouteEvents(current: Route, incoming: RouteEvent[]): Route {
  if (incoming.length === 0) return current;
  const existing = new Set((current.events ?? []).map(routeEventKey));
  const fresh = incoming.filter((event) => !existing.has(routeEventKey(event)));
  if (fresh.length === 0) return current;
  return { ...current, events: [...(current.events ?? []), ...fresh] };
}
